// ---------------------------------------------------------------------------
// Rules API Client — covers Rule CRUD, Library, Execution, and History.
//
// Repointed onto the v2 Unified Rule Provider at /api/v1/dq-rules
// (ztd_app/api/modules/dq_rules) — the single KB+DB rule source the
// profiler also uses. (The legacy /api/v2/rules Functional Rules Framework in
// backend/app is retired.) The provider returns the `UnifiedRule` shape inside
// an APIEnvelope {data, meta}; the adapters below map it to the Rule /
// LibraryEntry shapes the components already consume, so the UI is unchanged.
// ---------------------------------------------------------------------------

import { clearAuth } from "../auth";

const BASE = "/api/v1/dq-rules";

// Methods the backend's CSRF middleware requires a double-submit token for
// (mirrors SAFE_METHODS in ztd_app/api/core/middleware/csrf.py).
const CSRF_REQUIRED_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(CSRF_REQUIRED_METHODS.has(method)
      ? {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        }
      : {}),
    ...(init?.headers as Record<string, string>),
  };
  const res = await fetch(`${BASE}${url}`, {
    ...init,
    headers,
    credentials: "include",
  });
  if (res.status === 401) {
    clearAuth();
    throw new Error("Session expired — please log in again.");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RuleType =
  | "referential_integrity"
  | "range_domain"
  | "conditional"
  | "temporal_consistency"
  | "cross_table_aggregate"
  | "format_pattern"
  | "statistical_distribution";

export type RuleStatus =
  | "draft"
  | "validated"
  | "submitted"
  | "approved"
  | "rejected"
  | "retired";

export type RuleInputMode = "form" | "natural_language";

/** A knowledge pack in force for a project, and whether a steward adopted it. */
export interface KnowledgePack {
  pack_name: string;
  pack_version: string;
  engagement_slug: string | null;
  rule_count: number;
  adoption: {
    adopted_by: string;
    actor_project_role: string | null;
    adopted_at: string | null;
    note: string | null;
  } | null;
}

/** A member's DQ-rule role on a project (dq_rules/governance.py). */
export type ProjectRuleRole = "dq_analyst" | "data_steward";

/** What the caller may do with one project's rules (GET /access). */
export interface ProjectRuleAccess {
  user_id: string;
  project_id: string;
  project_role: ProjectRuleRole | "platform_admin" | null;
  project_role_label: string;
  is_platform_admin: boolean;
  permissions: string[];
}

export interface ProjectRuleMember {
  user_id: string;
  user_email: string;
  user_name: string;
  member_role: string;
  rule_role: ProjectRuleRole | null;
  rule_role_label: string | null;
}

export interface ProjectRuleRoles {
  members: ProjectRuleMember[];
  roles: { value: ProjectRuleRole; label: string }[];
}

export type ExecutionStatus = "pass" | "fail" | "error" | "skipped";

/** Four-eyes and approval facts of a project rule (DQ rule governance). */
export interface RuleGovernance {
  created_by: string;
  submitted_by: string | null;
  submitted_at: string | null;
  /** Who changed what runs since the last approval (they may not approve it). */
  logic_editors: string[];
  approved_by: string | null;
  approved_at: string | null;
  /** normal | override ("Approved by override") | pre_governance ("approved before governance") */
  approval_kind: "normal" | "override" | "pre_governance" | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  /** sha256 of the current payload, and of the approved version that runs. */
  fingerprint: string | null;
  approved_fingerprint: string | null;
}

export interface Rule {
  id: string;
  project_id: string;
  label: string;
  description: string;
  rule_type: RuleType;
  input_mode: RuleInputMode;
  structured_expression: Record<string, unknown>;
  nl_text: string;
  source_tables: string[];
  target_columns: string[];
  status: RuleStatus;
  is_cross_table: boolean;
  /** How the rule is evaluated, and the code that runs: raw SQL
   * (`condition_sql`, with `{schema}`) or the structured JSON DSL. */
  expr_kind: "structured" | "condition_sql";
  condition_sql: string | null;
  population_sql: string | null;
  /** Project rules are editable; KB pack rules are read-only (customize first). */
  editable: boolean;
  /** KB rule_key this project rule was copied from, if any. */
  derived_from: string | null;
  severity: string;
  query_timeout_seconds: number;
  max_sample_failures: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  /** Present for project rules (db:<uuid>); absent for pack rules. */
  governance?: RuleGovernance;
}

export interface RuleListResponse {
  rules: Rule[];
  total: number;
}

export interface LibraryEntry {
  id: string;
  project_id: string;
  source_rule_id: string | null;
  label: string;
  description: string;
  /** Knowledge pack the rule ships in, and its version (provenance). */
  pack_name: string;
  version: string;
  rule_type: RuleType;
  structured_expression: Record<string, unknown>;
  nl_text: string;
  domain_tags: string[];
  status: "active" | "retired";
  /** The rule's code, so the library can show what it runs. */
  expr_kind: "structured" | "condition_sql";
  condition_sql: string | null;
  population_sql: string | null;
  table_name: string | null;
  severity: string;
  execution_count: number;
  reuse_count: number;
  pass_count: number;
  fail_count: number;
  published_by: string;
  created_at: string;
  updated_at: string;
}

export interface LibraryListResponse {
  entries: LibraryEntry[];
  total: number;
}

// --- Unified Rule Provider (v2) shape + adapters ---------------------------
// The provider returns `UnifiedRule` inside APIEnvelope {data, meta}. We adapt
// to the Rule / LibraryEntry shapes the components already consume so the UI
// stays unchanged. `rule_key` is the stable id ("db:<uuid>" for DB rules, the
// pack rule_id for KB rules).
export interface UnifiedRule {
  rule_key: string;
  label: string;
  description: string;
  origin: "project" | "library" | "knowledge_base";
  severity: string;
  category: string;
  table_name: string | null;
  affected_columns: string[];
  expr_kind: "structured" | "condition_sql";
  condition_sql: string | null;
  population_sql?: string | null;
  structured_expression: Record<string, unknown> | null;
  source_ref: Record<string, unknown>;
  editable: boolean;
  derived_from: string | null;
  reuse_count: number;
}

interface APIEnvelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

function unifiedToRule(u: UnifiedRule): Rule {
  const ref = u.source_ref ?? {};
  const status =
    (ref.status as RuleStatus) ??
    (u.origin === "knowledge_base" ? "approved" : "draft");
  return {
    id: u.rule_key,
    project_id: (ref.project_id as string) ?? "",
    label: u.label,
    description: u.description,
    rule_type: (u.category as RuleType) ?? "conditional",
    input_mode: "form",
    structured_expression: u.structured_expression ?? {},
    nl_text: "",
    source_tables: u.table_name ? [u.table_name] : [],
    target_columns: u.affected_columns ?? [],
    status,
    is_cross_table: !u.table_name,
    expr_kind: u.expr_kind ?? "structured",
    condition_sql: u.condition_sql ?? null,
    population_sql: u.population_sql ?? null,
    editable: Boolean(u.editable),
    derived_from: u.derived_from ?? null,
    severity: u.severity ?? "",
    query_timeout_seconds: 30,
    max_sample_failures: 100,
    created_by: String(ref.created_by ?? ""),
    created_at: "",
    updated_at: "",
    ...(u.editable ? { governance: governanceOf(ref) } : {}),
  };
}

function str(v: unknown): string | null {
  return v === null || v === undefined || v === "" ? null : String(v);
}

function governanceOf(ref: Record<string, unknown>): RuleGovernance {
  return {
    created_by: String(ref.created_by ?? ""),
    submitted_by: str(ref.submitted_by),
    submitted_at: str(ref.submitted_at),
    logic_editors: Array.isArray(ref.logic_editors)
      ? ref.logic_editors.map(String)
      : [],
    approved_by: str(ref.approved_by),
    approved_at: str(ref.approved_at),
    approval_kind:
      (str(ref.approval_kind) as RuleGovernance["approval_kind"]) ?? null,
    rejected_by: str(ref.rejected_by),
    rejected_at: str(ref.rejected_at),
    rejection_reason: str(ref.rejection_reason),
    fingerprint: str(ref.fingerprint),
    approved_fingerprint: str(ref.approved_fingerprint),
  };
}

function unifiedToLibraryEntry(u: UnifiedRule): LibraryEntry {
  const ref = u.source_ref ?? {};
  return {
    id: u.rule_key,
    project_id: (ref.project_id as string) ?? "",
    source_rule_id: u.derived_from,
    label: u.label,
    description: u.description,
    pack_name: String(ref.pack_name ?? ""),
    version: String(ref.pack_version ?? ""),
    rule_type: (u.category as RuleType) ?? "conditional",
    structured_expression: u.structured_expression ?? {},
    nl_text: "",
    domain_tags: [],
    status: "active",
    expr_kind: u.expr_kind ?? "structured",
    condition_sql: u.condition_sql ?? null,
    population_sql: u.population_sql ?? null,
    table_name: u.table_name ?? null,
    severity: u.severity ?? "",
    execution_count: 0,
    reuse_count: u.reuse_count,
    pass_count: 0,
    fail_count: 0,
    published_by: String(ref.source ?? "knowledge_base"),
    created_at: "",
    updated_at: "",
  };
}

export interface SampleFailure {
  row_index: number | null;
  row_identifier: string | null;
  violation_type: string;
  column_name: string;
  expected: string;
  actual_category: string;
}

export interface RuleResult {
  id: string;
  rule_id: string;
  project_id: string;
  session_id: string | null;
  executor_type: string;
  source_description: string;
  status: ExecutionStatus;
  total_rows_evaluated: number;
  passing_rows: number;
  failing_rows: number;
  pass_rate: number;
  sample_failures: SampleFailure[];
  error_message: string;
  execution_time_ms: number;
  executed_at: string;
  executed_by: string;
  /** execute-direct: the saved connection the rule ran against. */
  source_connection?: SourceConnectionInfo;
}

export interface RuleResultListResponse {
  results: RuleResult[];
  total: number;
}

export interface RuleApproval {
  id: string;
  rule_id: string;
  submitted_by: string;
  submitted_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  decision: "pending" | "approved" | "rejected";
  feedback: string;
}

export interface ApprovalListResponse {
  approvals: RuleApproval[];
  total: number;
}

/** Which saved project connection a run used (never a credential). */
export interface SourceConnectionInfo {
  connection_id: string;
  name: string | null;
  db_type: string | null;
  host: string | null;
  database: string | null;
}

export interface BatchExecuteResponse {
  results: RuleResult[];
  total: number;
  source_description: string;
  source_connection?: SourceConnectionInfo;
}

/** "name — host/database" for the connection a run used. */
export function describeSourceConnection(
  info: SourceConnectionInfo | undefined,
): string {
  if (!info) return "";
  const where = [info.host, info.database].filter(Boolean).join("/");
  return [info.name, where].filter(Boolean).join(" — ");
}

export interface SchemaColumn {
  name: string;
  dtype?: string;
  nullable?: boolean;
  is_pk?: boolean;
  is_fk?: boolean;
  fk_target?: string;
}

export interface NLParseResult {
  success: boolean;
  rule_type_label: string;
  description: string;
  overall_confidence: number;
  confidence_level: string;
  nl_text: string;
  error_message: string;
  suggestions: string[];
  column_bindings: Array<{
    nl_reference: string;
    resolved_table: string;
    resolved_column: string;
    confidence: number;
  }>;
  expression?: Record<string, unknown>;
  /** "structured": `expression` is the JSON rule. "condition_sql": the IR
   * couldn't express the sentence (or SQL was asked for), so the LLM drafted
   * one read-only SELECT from schema metadata; it already passed the rule SQL
   * guard and is saved as a draft that needs four-eyes approval. */
  kind?: "structured" | "condition_sql";
  /** The model said the sentence needs SQL (structured parse only). */
  needs_sql?: boolean;
  condition_sql?: string;
  label?: string;
  table_name?: string;
  affected_columns?: string[];
  rationale?: string;
  /** Why the structured form wasn't used, when the SQL draft replaced it. */
  ir_error?: string;
  dialect?: string | null;
}

export interface CompiledRuleSql {
  /** One row per violating record; null for an aggregate rule. */
  violation_sql: string | null;
  /** The total / failing counts the executor reads. */
  count_sql: string;
  dialect: string;
  dialect_note?: string | null;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const rulesApi = {
  // CRUD — "My Rules" = the project's editable rules (origin=project). KB packs
  // are shown under the Library tab, not here.
  list: async (
    params: { project_id?: string; status?: string; rule_type?: string } = {},
  ): Promise<RuleListResponse> => {
    const qs = new URLSearchParams();
    if (params.project_id) qs.set("project_id", params.project_id);
    qs.set("origin", "project");
    const env = await request<APIEnvelope<UnifiedRule[]>>(`/?${qs.toString()}`);
    const rules = (env.data ?? []).map(unifiedToRule);
    return { rules, total: rules.length };
  },

  get: async (ruleId: string): Promise<Rule> => {
    const qs = new URLSearchParams({ rule_key: ruleId });
    const env = await request<APIEnvelope<UnifiedRule>>(
      `/detail?${qs.toString()}`,
    );
    return unifiedToRule(env.data);
  },

  create: async (body: {
    project_id: string;
    label: string;
    rule_type: RuleType;
    // Provide EITHER structured_expression (expr_kind=structured, default) OR
    // condition_sql with expr_kind="condition_sql" (a raw-SQL rule that runs
    // via the existing evaluator — used by Promote-to-rule).
    structured_expression?: Record<string, unknown>;
    expr_kind?: "structured" | "condition_sql";
    condition_sql?: string;
    nl_text?: string;
    input_mode?: RuleInputMode;
    description?: string;
    // The single table this rule targets (cross-table rules leave this
    // unset). Without it the backend persists an empty `source_tables`,
    // the rule shows a "no tables" badge, and depending on how it was
    // authored may run against the wrong data entirely.
    table_name?: string;
  }): Promise<Rule> => {
    const qs = new URLSearchParams({ project_id: body.project_id });
    const exprKind = body.expr_kind ?? "structured";
    const payload =
      exprKind === "condition_sql"
        ? {
            label: body.label,
            rule_type: body.rule_type,
            description: body.description ?? "",
            expr_kind: "condition_sql",
            condition_sql: body.condition_sql,
            table_name: body.table_name,
          }
        : {
            label: body.label,
            rule_type: body.rule_type,
            description: body.description ?? "",
            expr_kind: "structured",
            structured_expression: body.structured_expression ?? {},
            table_name: body.table_name,
          };
    const env = await request<APIEnvelope<UnifiedRule>>(`/?${qs.toString()}`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
    return unifiedToRule(env.data);
  },

  // Update an editable project rule. The provider returns the updated
  // UnifiedRule inside an APIEnvelope; we adapt back to the Rule shape.
  update: async (
    ruleId: string,
    body: {
      label?: string;
      structured_expression?: Record<string, unknown>;
      /** Raw-SQL rules: the new violation SQL (re-validated by the server). */
      expr_kind?: "structured" | "condition_sql";
      condition_sql?: string;
      description?: string;
    },
  ): Promise<Rule> => {
    const env = await request<APIEnvelope<UnifiedRule>>(
      `/${encodeURIComponent(ruleId)}`,
      { method: "PUT", body: JSON.stringify(body) },
    );
    return unifiedToRule(env.data);
  },

  // Retire (soft-delete) a project rule.
  delete: (ruleId: string) =>
    request<void>(`/${encodeURIComponent(ruleId)}`, { method: "DELETE" }),

  // NL parse — provider returns the NLParseResult inside an APIEnvelope.
  parseNL: async (body: {
    nl_text: string;
    schema_context: { tables: Record<string, SchemaColumn[]> };
    project_id?: string;
    /** Fall through to a raw-SQL draft when the structured IR can't express it. */
    allow_sql?: boolean;
    /** Draft SQL without trying the structured form first. */
    prefer_sql?: boolean;
    connection_id?: string;
    /** "file" for a file / folder session: its rules run in DuckDB. */
    source_kind?: "database" | "file";
  }): Promise<NLParseResult> => {
    const env = await request<APIEnvelope<NLParseResult>>("/parse-nl", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return env.data;
  },

  // The SQL a structured (JSON) rule compiles to, in the project source's
  // dialect with {schema}. Display only: nothing runs.
  compileSql: async (body: {
    structured_expression: Record<string, unknown>;
    project_id: string;
    connection_id?: string;
    source_kind?: "database" | "file";
  }): Promise<CompiledRuleSql> => {
    const env = await request<APIEnvelope<CompiledRuleSql>>("/compile-sql", {
      method: "POST",
      body: JSON.stringify(body),
    });
    return env.data;
  },

  // Execution — provider returns results inside an APIEnvelope.
  // The server connects only to a source connection saved on the project
  // (#844): `connection_id` optionally picks one of the project's saved source
  // connections, and no host/credentials are ever sent. `project_id` rides in
  // the query, where the route reads it.
  executeDirect: async (
    ruleId: string,
    opts: { project_id?: string; connection_id?: string } = {},
  ): Promise<RuleResult> => {
    const query = opts.project_id
      ? `?project_id=${encodeURIComponent(opts.project_id)}`
      : "";
    const env = await request<APIEnvelope<RuleResult>>(
      `/${encodeURIComponent(ruleId)}/execute-direct${query}`,
      {
        method: "POST",
        body: JSON.stringify(
          opts.connection_id ? { connection_id: opts.connection_id } : {},
        ),
      },
    );
    return env.data;
  },

  executeBatch: async (body: {
    rule_ids?: string[];
    project_id?: string;
    connection_id?: string;
    session_id?: string | null;
  }): Promise<BatchExecuteResponse> => {
    const env = await request<APIEnvelope<BatchExecuteResponse>>(
      "/execute-batch",
      { method: "POST", body: JSON.stringify(body) },
    );
    return env.data;
  },

  listResults: async (
    ruleId: string,
    limit = 20,
  ): Promise<RuleResultListResponse> => {
    const env = await request<APIEnvelope<RuleResultListResponse>>(
      `/${encodeURIComponent(ruleId)}/results?limit=${limit}`,
    );
    return env.data;
  },

  // Export to Backlog — uses the backlog router (different prefix, absolute URL)
  exportToBacklog: async (body: {
    rule_ids?: string[];
    project_id?: string;
    session_name?: string;
    additional_context?: string;
  }): Promise<{
    session_id: string;
    session_name: string;
    findings_count: number;
    document_summary: string;
    requirement_count: { functional: number; non_functional: number };
  }> => {
    const res = await fetch("/api/backlog/from-rule-findings", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
      },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (res.status === 401) {
      clearAuth();
      throw new Error("Session expired — please log in again.");
    }
    if (!res.ok) {
      const errBody = await res
        .json()
        .catch(() => ({ detail: res.statusText }));
      throw new Error(errBody.detail || `Request failed: ${res.status}`);
    }
    return res.json();
  },

  // Ignored Findings (Apr 2026 user request) — per-dataset suppression list.
  // Backend endpoints live under /api/profiling-agent/ignored-findings, not the
  // rules base prefix, so we use absolute URLs here.
  ignoredFindings: {
    create: async (body: {
      project_id: string;
      source_name: string;
      /** Live profiler session: the backend derives the canonical source key from it. */
      session_id?: string;
      finding?: Record<string, unknown>;
      signature?: string;
      reason?: string;
    }) => {
      const res = await fetch("/api/profiling-agent/ignored-findings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        clearAuth();
        throw new Error("Session expired");
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(e.detail || `Request failed: ${res.status}`);
      }
      return res.json();
    },
    list: async (project_id: string, source_name = "", session_id = "") => {
      const qs = new URLSearchParams({ project_id });
      if (source_name) qs.set("source_name", source_name);
      if (session_id) qs.set("session_id", session_id);
      const res = await fetch(`/api/profiling-agent/ignored-findings?${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json() as Promise<{ items: Array<Record<string, unknown>> }>;
    },
    remove: async (id: string) => {
      const res = await fetch(`/api/profiling-agent/ignored-findings/${id}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token":
            document.cookie.match(/ztd_csrf_token=([^;]+)/)?.[1] ?? "",
        },
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  },

  // DQ rule governance: rule powers come from the caller's role ON THE PROJECT
  // (project_members.rule_role), not from the tenant role.
  access: async (projectId: string): Promise<ProjectRuleAccess> => {
    const env = await request<APIEnvelope<ProjectRuleAccess>>(
      `/access?${new URLSearchParams({ project_id: projectId })}`,
    );
    return env.data;
  },
  packs: {
    list: async (projectId: string): Promise<KnowledgePack[]> => {
      const env = await request<APIEnvelope<KnowledgePack[]>>(
        `/packs?${new URLSearchParams({ project_id: projectId })}`,
      );
      return env.data ?? [];
    },
    adopt: async (
      projectId: string,
      packName: string,
      packVersion: string,
      note?: string,
    ): Promise<void> => {
      await request(
        `/packs/adopt?${new URLSearchParams({ project_id: projectId })}`,
        {
          method: "POST",
          body: JSON.stringify({
            pack_name: packName,
            pack_version: packVersion,
            note: note || null,
          }),
        },
      );
    },
  },
  projectRoles: {
    list: async (projectId: string): Promise<ProjectRuleRoles> => {
      const env = await request<APIEnvelope<ProjectRuleRoles>>(
        `/project-roles?${new URLSearchParams({ project_id: projectId })}`,
      );
      return env.data;
    },
    assign: async (
      projectId: string,
      userId: string,
      ruleRole: ProjectRuleRole | null,
    ): Promise<{ user_id: string; rule_role: ProjectRuleRole | null }> => {
      const env = await request<
        APIEnvelope<{ user_id: string; rule_role: ProjectRuleRole | null }>
      >(
        `/project-roles/${encodeURIComponent(userId)}?${new URLSearchParams({ project_id: projectId })}`,
        { method: "PUT", body: JSON.stringify({ rule_role: ruleRole }) },
      );
      return env.data;
    },
  },

  // Approval workflow — status transitions on the rule. The provider has no
  // separate approvals table; the rule's `status` IS the approval state, and
  // each endpoint returns a synthesized RuleApproval inside an APIEnvelope.
  approvals: {
    list: async (ruleId: string): Promise<ApprovalListResponse> => {
      const env = await request<APIEnvelope<RuleApproval[]>>(
        `/${encodeURIComponent(ruleId)}/approvals`,
      );
      const approvals = env.data ?? [];
      return { approvals, total: approvals.length };
    },
    submit: async (ruleId: string, note = ""): Promise<RuleApproval> => {
      const env = await request<APIEnvelope<RuleApproval>>(
        `/${encodeURIComponent(ruleId)}/submit`,
        { method: "POST", body: JSON.stringify({ note }) },
      );
      return env.data;
    },
    approve: async (ruleId: string, feedback = ""): Promise<RuleApproval> => {
      const env = await request<APIEnvelope<RuleApproval>>(
        `/${encodeURIComponent(ruleId)}/approve`,
        { method: "POST", body: JSON.stringify({ feedback }) },
      );
      return env.data;
    },
    /** Platform admin only: approve without a second person (justification >= 30 chars). */
    override: async (
      ruleId: string,
      justification: string,
      ticketRef?: string,
    ): Promise<RuleApproval> => {
      const env = await request<APIEnvelope<RuleApproval>>(
        `/${encodeURIComponent(ruleId)}/override-approve`,
        {
          method: "POST",
          body: JSON.stringify({
            justification,
            ticket_ref: ticketRef || null,
          }),
        },
      );
      return env.data;
    },
    reject: async (ruleId: string, feedback: string): Promise<RuleApproval> => {
      const env = await request<APIEnvelope<RuleApproval>>(
        `/${encodeURIComponent(ruleId)}/reject`,
        { method: "POST", body: JSON.stringify({ feedback }) },
      );
      return env.data;
    },
  },

  // Library
  library: {
    list: async (
      params: {
        project_id?: string;
        rule_type?: string;
        tag?: string;
        include_retired?: boolean;
      } = {},
    ): Promise<LibraryListResponse> => {
      // The "Rule Library" tab surfaces the engagement's KB rule packs
      // (origin=knowledge_base) resolved for this project. The provider derives
      // the engagement server-side from project.config, so no slug is passed.
      const qs = new URLSearchParams();
      if (params.project_id) qs.set("project_id", params.project_id);
      qs.set("origin", "knowledge_base");
      const env = await request<APIEnvelope<UnifiedRule[]>>(
        `/?${qs.toString()}`,
      );
      const entries = (env.data ?? []).map(unifiedToLibraryEntry);
      return { entries, total: entries.length };
    },

    get: (entryId: string) => request<LibraryEntry>(`/library/${entryId}`),

    publish: (body: {
      rule_id: string;
      domain_tags?: string[];
      is_cross_program?: boolean;
      version?: string;
    }) =>
      request<LibraryEntry>("/library", {
        method: "POST",
        body: JSON.stringify(body),
      }),

    // Copy-on-write a KB rule (`entryId` = its rule_key) into an editable
    // project draft via the real Unified Rule Provider endpoint. There is no
    // `/library/*` sub-route on the backend — this is the same `/customize`
    // call the Rules tab's "Promote" flow would use.
    reuse: async (
      entryId: string,
      body: { target_project_id: string; label_override?: string },
    ): Promise<Rule> => {
      const qs = new URLSearchParams({
        rule_key: entryId,
        project_id: body.target_project_id,
      });
      if (body.label_override) qs.set("label", body.label_override);
      const env = await request<APIEnvelope<UnifiedRule>>(
        `/customize?${qs.toString()}`,
        { method: "POST" },
      );
      return unifiedToRule(env.data);
    },
  },
};
